import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, LogOut, Users, TrendingUp, Lightbulb, BarChart3 } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

export default function ManagerDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const response = await api.get('/hr/manager/my-team');
      setTeamData(response.data);
      if (response.data.insights) {
        setInsights(response.data.insights);
      }
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Ekip verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const pieChartData = teamData?.analytics?.distribution?.map(item => ({
    name: item.name,
    value: item.count,
    percentage: item.percentage
  })) || [];

  const barChartData = teamData?.analytics?.distribution?.map(item => ({
    name: item.name.split(' / ')[0],
    count: item.count
  })) || [];

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Yönetici Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <Badge className="bg-indigo-100 text-indigo-700">Yönetici</Badge>
            <span className="text-sm text-slate-600">{user.name}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/coach')}
              className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 hover:bg-blue-100"
            >
              💬 AI Koç
            </Button>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Ekip Yönetimi</h2>
          <p className="text-slate-600">Ekibinizin mizaç profilini keşfedin ve liderlik stratejilerinizi geliştirin</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : teamData?.team_size === 0 ? (
          <Card className="shadow-lg border-none">
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Henüz size rapor veren çalışan bulunmuyor</p>
              <p className="text-sm text-slate-400">İK departmanı ile iletişime geçin</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="shadow-lg border-none">
                <CardHeader className="pb-3">
                  <CardDescription>Ekip Büyüklüğü</CardDescription>
                  <CardTitle className="text-3xl">{teamData.team_size}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="shadow-lg border-none">
                <CardHeader className="pb-3">
                  <CardDescription>Tamamlanan Değerlendirme</CardDescription>
                  <CardTitle className="text-3xl text-emerald-600">
                    {teamData.analytics?.employees_with_reports || 0}
                  </CardTitle>
                </CardHeader>
              </Card>
              <Card className="shadow-lg border-none">
                <CardHeader className="pb-3">
                  <CardDescription>Tamamlanma Oranı</CardDescription>
                  <CardTitle className="text-3xl text-blue-600">
                    {teamData.team_size > 0
                      ? Math.round((teamData.analytics?.employees_with_reports / teamData.team_size) * 100)
                      : 0}%
                  </CardTitle>
                </CardHeader>
              </Card>
            </div>

            {/* Charts */}
            {teamData.analytics?.employees_with_reports > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="shadow-lg border-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      Ekip Mizaç Dağılımı
                    </CardTitle>
                    <CardDescription>Ekibinizdeki mizaç tiplerinin oranı</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name.split(' / ')[0]}: ${percentage}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="shadow-lg border-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Mizaç Karşılaştırması
                    </CardTitle>
                    <CardDescription>Çalışan sayılarının dağılımı</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={barChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#6366f1" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* AI Insights */}
            {insights && (
              <Card className="shadow-xl border-none">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-600" />
                    AI Destekli Yönetim Önerileri
                  </CardTitle>
                  <CardDescription>Ekibiniz için kişiselleştirilmiş liderlik stratejileri</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg border border-yellow-200">
                    <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">{insights}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Members */}
            <Card className="shadow-lg border-none">
              <CardHeader>
                <CardTitle>Ekip Üyeleri</CardTitle>
                <CardDescription>Doğrudan size rapor veren çalışanlar</CardDescription>
              </CardHeader>
              <CardContent>
                {teamData.team_members && teamData.team_members.length > 0 ? (
                  <div className="space-y-3">
                    {teamData.team_members.map((member, idx) => (
                      <div key={idx} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div>
                          <p className="font-semibold text-slate-800">{member.name}</p>
                          <p className="text-sm text-slate-600">{member.email}</p>
                          {member.department && (
                            <Badge variant="outline" className="mt-1 text-xs">{member.department}</Badge>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/employee/${member.id}`)}
                        >
                          Profili Gör
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Henüz ekip üyesi bilgisi yok</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
